import React, { useState, useRef, useEffect, useCallback, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { WorkspaceAPI, PERSONAL_WORKSPACE_ID } from '../../../utils/api';
import { useAuth } from '../../AuthProvider';
import { useSubscription } from '../../../context/SubscriptionContext';
import { isWorkspaceCovered } from '../../../utils/workspaceAccess';
import { BrainIcon } from '../icons';

/**
 * Workspace switcher fixed above the sidebar Search control.
 */
function peopleLabel(count) {
  const n = Number(count);
  if (!Number.isFinite(n) || n < 1) return 'People';
  if (n === 1) return 'Just you';
  return `${n} people`;
}

const SidebarWorkspaceSwitcher = ({
  workspaceId,
  workspaces = [],
  setWorkspace,
  onOpenSettings,
  embedded = false,
  variant = 'sidebar',
}) => {
  const { refreshWorkspaces } = useAuth();
  const { complimentary } = useSubscription();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState('');
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (creating && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [creating]);

  const visibleWorkspaces = useMemo(() => {
    const list = workspaces || [];
    const named = list.filter((w) => w.id && w.id !== PERSONAL_WORKSPACE_ID);
    const rank = (w) => (isWorkspaceCovered(w) ? 0 : 1);
    return [...named].sort(
      (a, b) => rank(a) - rank(b) || (a.name || '').localeCompare(b.name || '')
    );
  }, [workspaces]);

  const currentLabel = useMemo(() => {
    const ws =
      visibleWorkspaces.find((w) => w.id === workspaceId) ||
      (workspaces || []).find((w) => w.id === workspaceId);
    return ws?.name || 'Brain';
  }, [visibleWorkspaces, workspaces, workspaceId]);

  const resetCreate = useCallback(() => {
    setCreating(false);
    setNewName('');
    setCreateError('');
  }, []);

  const handleCreate = useCallback(async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setCreateError('');
    try {
      const res = await WorkspaceAPI.create(trimmed);
      if (res.success && res.workspace) {
        await refreshWorkspaces();
        setWorkspace(res.workspace.id);
      }
      resetCreate();
      setOpen(false);
    } catch (err) {
      setCreateError(err.message || 'Failed to create');
    }
  }, [newName, refreshWorkspaces, setWorkspace, resetCreate]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const [menuPos, setMenuPos] = useState(null);

  const updateMenuPos = useCallback(() => {
    if (!open || !buttonRef.current) {
      setMenuPos(null);
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 6,
      left: rect.left,
      minWidth: Math.max(rect.width, 240),
    });
  }, [open]);

  useLayoutEffect(() => {
    updateMenuPos();
  }, [updateMenuPos, workspaceId, workspaces]);

  useEffect(() => {
    if (!open) return;
    const onResize = () => updateMenuPos();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, updateMenuPos]);

  return (
    <>
      <div
        className={`sidebar__ws-switcher${embedded ? ' sidebar__ws-switcher--embedded' : ''}${
          variant === 'navbar' ? ' sidebar__ws-switcher--navbar' : ''
        }`}
      >
        <button
          ref={buttonRef}
          type="button"
          className={`sidebar__ws-trigger ${open ? 'sidebar__ws-trigger--open' : ''}`}
          onClick={() => setOpen((o) => !o)}
          title="Switch brain"
        >
          {!(embedded || variant === 'navbar') && <BrainIcon size={13} />}
          <span className="sidebar__ws-trigger-label">{currentLabel}</span>
          <svg className="sidebar__ws-trigger-chevron" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>

      {open &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            className="sidebar-user__menu sidebar__ws-dropdown"
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              minWidth: menuPos.minWidth,
              zIndex: 10050,
            }}
          >
            <div className="sidebar-user__menu-subheading">Brains</div>
            {visibleWorkspaces.map((ws) => {
              const named = ws.id && ws.id !== PERSONAL_WORKSPACE_ID;
              return (
                <div
                  key={ws.id}
                  className={`sidebar__ws-row ${
                    ws.id === workspaceId ? 'sidebar__ws-row--active' : ''
                  }`}
                >
                  <button
                    type="button"
                    className="sidebar-user__menu-item sidebar__ws-row-main"
                    onClick={() => {
                      if (!setWorkspace || ws.id === workspaceId) {
                        setOpen(false);
                        return;
                      }
                      setWorkspace(ws.id);
                      setOpen(false);
                    }}
                    title={ws.name || ws.id}
                  >
                    <BrainIcon size={14} />
                    <span className="sidebar__ws-row-copy">
                      <span className="sidebar-user__workspace-name">{ws.name || 'Brain'}</span>
                      {named && (
                        <span className="sidebar__ws-row-meta">{peopleLabel(ws.memberCount)}</span>
                      )}
                    </span>
                  </button>
                  {named && onOpenSettings && (
                    <button
                      type="button"
                      className="sidebar__ws-people"
                      title="Open People"
                      onClick={() => {
                        if (setWorkspace && ws.id !== workspaceId) {
                          setWorkspace(ws.id);
                        }
                        setOpen(false);
                        onOpenSettings();
                      }}
                    >
                      People
                    </button>
                  )}
                </div>
              );
            })}

            {creating ? (
              <div className="sidebar-user__create-ws">
                <input
                  ref={nameInputRef}
                  type="text"
                  className="sidebar-user__create-ws-input"
                  placeholder="Brain name"
                  value={newName}
                  maxLength={64}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreate();
                    if (e.key === 'Escape') {
                      resetCreate();
                    }
                  }}
                />
                <div className="sidebar-user__create-ws-actions">
                  <button
                    type="button"
                    className="sidebar-user__action-btn--create"
                    onClick={handleCreate}
                    disabled={!newName.trim()}
                  >
                    Create
                  </button>
                  <button
                    type="button"
                    className="sidebar-user__action-btn--cancel"
                    onClick={resetCreate}
                  >
                    Cancel
                  </button>
                </div>
                {createError && <span className="sidebar-user__create-ws-error">{createError}</span>}
                {!complimentary && (
                  <span className="sidebar-user__create-ws-hint">
                    A new brain is a new room. Pick Solo or Team when you get there.
                  </span>
                )}
              </div>
            ) : (
              <button
                type="button"
                className="sidebar-user__menu-item sidebar-user__menu-item--add"
                onClick={() => {
                  setCreating(true);
                  setCreateError('');
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>Create brain</span>
              </button>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

export default SidebarWorkspaceSwitcher;
